// Scheduling Service - Comprehensive scheduling and rescheduling for assessments and interviews
import AsyncStorage from '@react-native-async-storage/async-storage';

class SchedulingService {
  constructor() {
    this.schedules = new Map();
    this.availabilitySlots = new Map();
    this.notifications = new Map();
    this.timeZone = 'Europe/London'; // Default to UK timezone
  }

  // Initialize scheduling system
  async initializeScheduling(candidateId, jobId, assessmentTypes = []) {
    try {
      const schedulingId = `schedule_${candidateId}_${jobId}_${Date.now()}`;
      
      const schedule = {
        schedulingId,
        candidateId,
        jobId,
        assessmentTypes,
        status: 'pending_schedule',
        appointments: [],
        preferences: {
          timeZone: this.timeZone,
          preferredTimes: ['morning', 'afternoon'], // morning, afternoon, evening
          preferredDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          bufferTime: 15, // minutes between appointments
          maxDuration: 180, // maximum assessment duration in minutes
          allowWeekends: false,
          allowEvenings: false
        },
        notifications: {
          email: true,
          sms: false,
          push: true,
          reminderTimes: [24, 2, 0.5] // hours before appointment
        },
        createdAt: new Date().toISOString()
      };

      // Generate available time slots
      schedule.availableSlots = await this.generateAvailableSlots(schedule);
      
      this.schedules.set(schedulingId, schedule);
      await this.saveScheduleToStorage(schedulingId, schedule);

      return schedule;
    } catch (error) {
      console.error('Error initializing scheduling:', error);
      throw new Error('Failed to initialize scheduling system');
    }
  }

  // Generate available time slots
  async generateAvailableSlots(schedule) {
    try {
      const slots = [];
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(startDate.getDate() + 14); // 2 weeks ahead

      // Generate slots for each assessment type
      for (const assessmentType of schedule.assessmentTypes) {
        const duration = this.getAssessmentDuration(assessmentType);
        const typeSlots = await this.generateSlotsForAssessment(
          assessmentType,
          duration,
          startDate,
          endDate,
          schedule.preferences
        );
        slots.push(...typeSlots);
      }

      return slots.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    } catch (error) {
      console.error('Error generating available slots:', error);
      return [];
    }
  }

  // Get assessment duration based on type
  getAssessmentDuration(assessmentType) {
    const durations = {
      'technical_test': 120, // 2 hours
      'ai_interview': 45,    // 45 minutes
      'psychometric_test': 60, // 1 hour
      'ar_portfolio': 30,    // 30 minutes
      'final_interview': 60, // 1 hour
      'panel_interview': 90, // 1.5 hours
      'technical_interview': 75, // 1.25 hours
      'hr_interview': 45,    // 45 minutes
      'culture_fit': 30      // 30 minutes
    };
    
    return durations[assessmentType] || 60;
  }

  // Generate slots for specific assessment
  async generateSlotsForAssessment(assessmentType, duration, startDate, endDate, preferences) {
    const slots = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      // Skip weekends if not allowed
      if (!preferences.allowWeekends && (currentDate.getDay() === 0 || currentDate.getDay() === 6)) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      // Skip if not preferred day
      const dayName = this.getDayName(currentDate.getDay());
      if (!preferences.preferredDays.includes(dayName)) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      // Generate time slots for the day
      const daySlots = this.generateDaySlotsForAssessment(
        currentDate,
        assessmentType,
        duration,
        preferences
      );
      
      slots.push(...daySlots);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return slots;
  }

  // Generate time slots for a specific day
  generateDaySlotsForAssessment(date, assessmentType, duration, preferences) {
    const slots = [];
    const timeSlots = this.getTimeSlots(preferences);

    for (const timeSlot of timeSlots) {
      const slotStart = new Date(date);
      slotStart.setHours(timeSlot.hour, timeSlot.minute, 0, 0);
      
      const slotEnd = new Date(slotStart);
      slotEnd.setMinutes(slotEnd.getMinutes() + duration);

      // Check if slot is in the future
      if (slotStart <= new Date()) {
        continue;
      }

      // Check if slot fits within working hours
      if (this.isWithinWorkingHours(slotStart, slotEnd, preferences)) {
        slots.push({
          id: `slot_${assessmentType}_${slotStart.getTime()}`,
          assessmentType,
          startTime: slotStart.toISOString(),
          endTime: slotEnd.toISOString(),
          duration,
          available: true,
          timeSlot: timeSlot.label,
          dayOfWeek: this.getDayName(date.getDay()),
          date: date.toISOString().split('T')[0]
        });
      }
    }

    return slots;
  }

  // Get available time slots based on preferences
  getTimeSlots(preferences) {
    const slots = [];
    
    // Morning slots (9 AM - 12 PM)
    if (preferences.preferredTimes.includes('morning')) {
      slots.push(
        { hour: 9, minute: 0, label: 'morning' },
        { hour: 9, minute: 30, label: 'morning' },
        { hour: 10, minute: 0, label: 'morning' },
        { hour: 10, minute: 30, label: 'morning' },
        { hour: 11, minute: 0, label: 'morning' },
        { hour: 11, minute: 30, label: 'morning' }
      );
    }

    // Afternoon slots (1 PM - 5 PM)
    if (preferences.preferredTimes.includes('afternoon')) {
      slots.push(
        { hour: 13, minute: 0, label: 'afternoon' },
        { hour: 13, minute: 30, label: 'afternoon' },
        { hour: 14, minute: 0, label: 'afternoon' },
        { hour: 14, minute: 30, label: 'afternoon' },
        { hour: 15, minute: 0, label: 'afternoon' },
        { hour: 15, minute: 30, label: 'afternoon' },
        { hour: 16, minute: 0, label: 'afternoon' },
        { hour: 16, minute: 30, label: 'afternoon' }
      );
    }

    // Evening slots (6 PM - 8 PM)
    if (preferences.allowEvenings && preferences.preferredTimes.includes('evening')) {
      slots.push(
        { hour: 18, minute: 0, label: 'evening' },
        { hour: 18, minute: 30, label: 'evening' },
        { hour: 19, minute: 0, label: 'evening' },
        { hour: 19, minute: 30, label: 'evening' }
      );
    }

    return slots;
  }

  // Check if time slot is within working hours
  isWithinWorkingHours(startTime, endTime, preferences) {
    const startHour = startTime.getHours();
    const endHour = endTime.getHours();
    
    // Standard working hours: 9 AM - 6 PM
    const workingStart = 9;
    const workingEnd = preferences.allowEvenings ? 20 : 18;
    
    return startHour >= workingStart && endHour <= workingEnd;
  }

  // Get day name from day number
  getDayName(dayNumber) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[dayNumber];
  }

  // Schedule appointment
  async scheduleAppointment(schedulingId, slotId, additionalInfo = {}) {
    try {
      const schedule = this.schedules.get(schedulingId);
      if (!schedule) {
        throw new Error('Schedule not found');
      }

      const slot = schedule.availableSlots.find(s => s.id === slotId);
      if (!slot) {
        throw new Error('Time slot not found');
      }

      if (!slot.available) {
        throw new Error('Time slot is no longer available');
      }

      // Create appointment
      const appointment = {
        id: `appointment_${Date.now()}`,
        schedulingId,
        slotId,
        assessmentType: slot.assessmentType,
        startTime: slot.startTime,
        endTime: slot.endTime,
        duration: slot.duration,
        status: 'scheduled',
        candidateId: schedule.candidateId,
        jobId: schedule.jobId,
        additionalInfo,
        meetingDetails: await this.generateMeetingDetails(slot, schedule),
        reminders: await this.scheduleReminders(slot, schedule),
        createdAt: new Date().toISOString()
      };

      // Mark slot as unavailable
      slot.available = false;
      slot.appointmentId = appointment.id;

      // Add appointment to schedule
      schedule.appointments.push(appointment);
      schedule.status = 'scheduled';

      // Update schedule
      this.schedules.set(schedulingId, schedule);
      await this.saveScheduleToStorage(schedulingId, schedule);

      // Send confirmation
      await this.sendAppointmentConfirmation(appointment, schedule);

      return appointment;
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      throw new Error('Failed to schedule appointment');
    }
  }

  // Generate meeting details
  async generateMeetingDetails(slot, schedule) {
    const meetingDetails = {
      type: slot.assessmentType,
      platform: this.getMeetingPlatform(slot.assessmentType),
      location: this.getMeetingLocation(slot.assessmentType),
      instructions: this.getMeetingInstructions(slot.assessmentType),
      requirements: this.getMeetingRequirements(slot.assessmentType),
      contactInfo: {
        recruiter: {
          name: 'Sarah Johnson',
          email: 'sarah.johnson@company.com',
          phone: '+44 20 1234 5678'
        },
        technical: {
          name: 'IT Support',
          email: 'support@company.com',
          phone: '+44 20 1234 5679'
        }
      }
    };

    // Generate meeting links for online assessments
    if (meetingDetails.platform === 'online') {
      meetingDetails.meetingLink = await this.generateMeetingLink(slot, schedule);
      meetingDetails.meetingId = `meeting_${Date.now()}`;
      meetingDetails.passcode = this.generatePasscode();
    }

    return meetingDetails;
  }

  // Get meeting platform based on assessment type
  getMeetingPlatform(assessmentType) {
    const platforms = {
      'technical_test': 'online',
      'ai_interview': 'online',
      'psychometric_test': 'online',
      'ar_portfolio': 'online',
      'final_interview': 'hybrid', // Can be online or in-person
      'panel_interview': 'in-person',
      'technical_interview': 'online',
      'hr_interview': 'online',
      'culture_fit': 'in-person'
    };
    
    return platforms[assessmentType] || 'online';
  }

  // Get meeting location
  getMeetingLocation(assessmentType) {
    const platform = this.getMeetingPlatform(assessmentType);
    
    if (platform === 'online') {
      return 'Video Conference (link will be provided)';
    } else if (platform === 'in-person') {
      return 'Company Office - 123 Tech Street, London, EC1A 1BB';
    } else {
      return 'To be confirmed (online or in-person)';
    }
  }

  // Get meeting instructions
  getMeetingInstructions(assessmentType) {
    const instructions = {
      'technical_test': [
        'Ensure stable internet connection',
        'Use a computer with webcam and microphone',
        'Have your IDE ready (VS Code recommended)',
        'Join 5 minutes early for technical check'
      ],
      'ai_interview': [
        'Find a quiet, well-lit environment',
        'Test your camera and microphone beforehand',
        'Have a glass of water nearby',
        'Dress professionally',
        'Join 5 minutes early'
      ],
      'psychometric_test': [
        'Find a quiet environment without distractions',
        'Ensure stable internet connection',
        'Have a pen and paper for notes',
        'Read instructions carefully before starting'
      ],
      'ar_portfolio': [
        'Ensure your device supports AR features',
        'Have good lighting in your environment',
        'Clear a space for AR interactions',
        'Test AR functionality beforehand'
      ],
      'final_interview': [
        'Prepare questions about the role and company',
        'Review your portfolio and experiences',
        'Dress professionally',
        'Arrive 10 minutes early'
      ]
    };
    
    return instructions[assessmentType] || [
      'Join the meeting on time',
      'Ensure stable internet connection',
      'Have a quiet environment',
      'Test your audio and video beforehand'
    ];
  }

  // Get meeting requirements
  getMeetingRequirements(assessmentType) {
    const requirements = {
      'technical_test': [
        'Computer with webcam and microphone',
        'Stable internet connection (minimum 10 Mbps)',
        'Modern web browser (Chrome, Firefox, Safari)',
        'Code editor (VS Code, IntelliJ, etc.)'
      ],
      'ai_interview': [
        'Device with camera and microphone',
        'Stable internet connection',
        'Quiet environment',
        'Professional attire'
      ],
      'psychometric_test': [
        'Computer or tablet',
        'Stable internet connection',
        'Quiet environment',
        'Pen and paper for notes'
      ],
      'ar_portfolio': [
        'AR-capable device (smartphone/tablet)',
        'Good lighting',
        'Clear space for AR interactions',
        'Latest version of the app'
      ]
    };
    
    return requirements[assessmentType] || [
      'Device with internet connection',
      'Quiet environment',
      'Professional setup'
    ];
  }

  // Generate meeting link
  async generateMeetingLink(slot, schedule) {
    // Simulate meeting link generation
    const meetingId = `meeting_${Date.now()}`;
    return `https://meet.company.com/join/${meetingId}`;
  }

  // Generate passcode
  generatePasscode() {
    return Math.random().toString(36).substr(2, 8).toUpperCase();
  }

  // Schedule reminders
  async scheduleReminders(slot, schedule) {
    const reminders = [];
    const appointmentTime = new Date(slot.startTime);
    
    for (const hours of schedule.notifications.reminderTimes) {
      const reminderTime = new Date(appointmentTime);
      reminderTime.setHours(reminderTime.getHours() - hours);
      
      // Only schedule future reminders
      if (reminderTime > new Date()) {
        reminders.push({
          id: `reminder_${Date.now()}_${hours}h`,
          type: hours >= 1 ? 'advance' : 'immediate',
          scheduledTime: reminderTime.toISOString(),
          message: this.generateReminderMessage(hours, slot),
          channels: this.getReminderChannels(hours, schedule.notifications),
          status: 'scheduled'
        });
      }
    }
    
    return reminders;
  }

  // Generate reminder message
  generateReminderMessage(hours, slot) {
    const assessmentName = this.getAssessmentDisplayName(slot.assessmentType);
    const startTime = new Date(slot.startTime).toLocaleString('en-GB', {
      timeZone: this.timeZone,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    if (hours >= 24) {
      return `Reminder: You have a ${assessmentName} scheduled for tomorrow at ${startTime}. Please prepare accordingly.`;
    } else if (hours >= 1) {
      return `Reminder: Your ${assessmentName} starts in ${hours} hour${hours > 1 ? 's' : ''} at ${startTime}. Please join on time.`;
    } else {
      const minutes = Math.round(hours * 60);
      return `Final reminder: Your ${assessmentName} starts in ${minutes} minutes. Please join now.`;
    }
  }

  // Get assessment display name
  getAssessmentDisplayName(assessmentType) {
    const names = {
      'technical_test': 'Technical Assessment',
      'ai_interview': 'AI Interview',
      'psychometric_test': 'Psychometric Test',
      'ar_portfolio': 'AR Portfolio Review',
      'final_interview': 'Final Interview',
      'panel_interview': 'Panel Interview',
      'technical_interview': 'Technical Interview',
      'hr_interview': 'HR Interview',
      'culture_fit': 'Culture Fit Interview'
    };
    
    return names[assessmentType] || 'Assessment';
  }

  // Get reminder channels
  getReminderChannels(hours, notificationSettings) {
    const channels = [];
    
    if (hours >= 24 && notificationSettings.email) {
      channels.push('email');
    }
    
    if (hours <= 2 && notificationSettings.push) {
      channels.push('push');
    }
    
    if (hours <= 0.5 && notificationSettings.sms) {
      channels.push('sms');
    }
    
    return channels;
  }

  // Reschedule appointment
  async rescheduleAppointment(appointmentId, newSlotId, reason = '') {
    try {
      // Find the appointment
      let schedule = null;
      let appointment = null;
      
      for (const [schedulingId, scheduleData] of this.schedules) {
        const foundAppointment = scheduleData.appointments.find(apt => apt.id === appointmentId);
        if (foundAppointment) {
          schedule = scheduleData;
          appointment = foundAppointment;
          break;
        }
      }

      if (!appointment || !schedule) {
        throw new Error('Appointment not found');
      }

      // Check if rescheduling is allowed
      const appointmentTime = new Date(appointment.startTime);
      const now = new Date();
      const hoursUntilAppointment = (appointmentTime - now) / (1000 * 60 * 60);
      
      if (hoursUntilAppointment < 24) {
        throw new Error('Cannot reschedule appointments less than 24 hours in advance');
      }

      // Find new slot
      const newSlot = schedule.availableSlots.find(s => s.id === newSlotId);
      if (!newSlot) {
        throw new Error('New time slot not found');
      }

      if (!newSlot.available) {
        throw new Error('New time slot is not available');
      }

      // Free up old slot
      const oldSlot = schedule.availableSlots.find(s => s.id === appointment.slotId);
      if (oldSlot) {
        oldSlot.available = true;
        delete oldSlot.appointmentId;
      }

      // Update appointment
      const oldStartTime = appointment.startTime;
      appointment.startTime = newSlot.startTime;
      appointment.endTime = newSlot.endTime;
      appointment.slotId = newSlotId;
      appointment.status = 'rescheduled';
      appointment.rescheduleHistory = appointment.rescheduleHistory || [];
      appointment.rescheduleHistory.push({
        oldStartTime,
        newStartTime: newSlot.startTime,
        reason,
        rescheduledAt: new Date().toISOString()
      });

      // Mark new slot as unavailable
      newSlot.available = false;
      newSlot.appointmentId = appointmentId;

      // Update meeting details
      appointment.meetingDetails = await this.generateMeetingDetails(newSlot, schedule);
      
      // Reschedule reminders
      appointment.reminders = await this.scheduleReminders(newSlot, schedule);

      // Update schedule
      this.schedules.set(schedule.schedulingId, schedule);
      await this.saveScheduleToStorage(schedule.schedulingId, schedule);

      // Send reschedule confirmation
      await this.sendRescheduleConfirmation(appointment, schedule, reason);

      return appointment;
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      throw new Error('Failed to reschedule appointment');
    }
  }

  // Cancel appointment
  async cancelAppointment(appointmentId, reason = '') {
    try {
      // Find the appointment
      let schedule = null;
      let appointment = null;
      let appointmentIndex = -1;
      
      for (const [schedulingId, scheduleData] of this.schedules) {
        appointmentIndex = scheduleData.appointments.findIndex(apt => apt.id === appointmentId);
        if (appointmentIndex !== -1) {
          schedule = scheduleData;
          appointment = scheduleData.appointments[appointmentIndex];
          break;
        }
      }

      if (!appointment || !schedule) {
        throw new Error('Appointment not found');
      }

      // Free up the slot
      const slot = schedule.availableSlots.find(s => s.id === appointment.slotId);
      if (slot) {
        slot.available = true;
        delete slot.appointmentId;
      }

      // Update appointment status
      appointment.status = 'cancelled';
      appointment.cancelledAt = new Date().toISOString();
      appointment.cancellationReason = reason;

      // Update schedule
      this.schedules.set(schedule.schedulingId, schedule);
      await this.saveScheduleToStorage(schedule.schedulingId, schedule);

      // Send cancellation confirmation
      await this.sendCancellationConfirmation(appointment, schedule, reason);

      return appointment;
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      throw new Error('Failed to cancel appointment');
    }
  }

  // Send appointment confirmation
  async sendAppointmentConfirmation(appointment, schedule) {
    try {
      const confirmation = {
        type: 'appointment_confirmation',
        appointmentId: appointment.id,
        candidateId: schedule.candidateId,
        jobId: schedule.jobId,
        subject: `Appointment Confirmed: ${this.getAssessmentDisplayName(appointment.assessmentType)}`,
        message: this.generateConfirmationMessage(appointment),
        scheduledTime: appointment.startTime,
        meetingDetails: appointment.meetingDetails,
        instructions: appointment.meetingDetails.instructions,
        requirements: appointment.meetingDetails.requirements,
        sentAt: new Date().toISOString()
      };

      // Store notification
      this.notifications.set(confirmation.appointmentId, confirmation);
      await this.saveNotificationToStorage(confirmation.appointmentId, confirmation);

      return confirmation;
    } catch (error) {
      console.error('Error sending confirmation:', error);
    }
  }

  // Generate confirmation message
  generateConfirmationMessage(appointment) {
    const assessmentName = this.getAssessmentDisplayName(appointment.assessmentType);
    const startTime = new Date(appointment.startTime).toLocaleString('en-GB', {
      timeZone: this.timeZone,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    return `Your ${assessmentName} has been scheduled for ${startTime}. Please review the meeting details and requirements below. We look forward to meeting with you!`;
  }

  // Send reschedule confirmation
  async sendRescheduleConfirmation(appointment, schedule, reason) {
    try {
      const confirmation = {
        type: 'reschedule_confirmation',
        appointmentId: appointment.id,
        candidateId: schedule.candidateId,
        jobId: schedule.jobId,
        subject: `Appointment Rescheduled: ${this.getAssessmentDisplayName(appointment.assessmentType)}`,
        message: this.generateRescheduleMessage(appointment, reason),
        newScheduledTime: appointment.startTime,
        meetingDetails: appointment.meetingDetails,
        sentAt: new Date().toISOString()
      };

      // Store notification
      this.notifications.set(`reschedule_${appointment.id}`, confirmation);
      await this.saveNotificationToStorage(`reschedule_${appointment.id}`, confirmation);

      return confirmation;
    } catch (error) {
      console.error('Error sending reschedule confirmation:', error);
    }
  }

  // Generate reschedule message
  generateRescheduleMessage(appointment, reason) {
    const assessmentName = this.getAssessmentDisplayName(appointment.assessmentType);
    const newTime = new Date(appointment.startTime).toLocaleString('en-GB', {
      timeZone: this.timeZone,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    let message = `Your ${assessmentName} has been rescheduled to ${newTime}.`;
    
    if (reason) {
      message += ` Reason: ${reason}.`;
    }
    
    message += ' Please review the updated meeting details below.';
    
    return message;
  }

  // Send cancellation confirmation
  async sendCancellationConfirmation(appointment, schedule, reason) {
    try {
      const confirmation = {
        type: 'cancellation_confirmation',
        appointmentId: appointment.id,
        candidateId: schedule.candidateId,
        jobId: schedule.jobId,
        subject: `Appointment Cancelled: ${this.getAssessmentDisplayName(appointment.assessmentType)}`,
        message: this.generateCancellationMessage(appointment, reason),
        cancelledTime: appointment.startTime,
        sentAt: new Date().toISOString()
      };

      // Store notification
      this.notifications.set(`cancellation_${appointment.id}`, confirmation);
      await this.saveNotificationToStorage(`cancellation_${appointment.id}`, confirmation);

      return confirmation;
    } catch (error) {
      console.error('Error sending cancellation confirmation:', error);
    }
  }

  // Generate cancellation message
  generateCancellationMessage(appointment, reason) {
    const assessmentName = this.getAssessmentDisplayName(appointment.assessmentType);
    
    let message = `Your ${assessmentName} has been cancelled.`;
    
    if (reason) {
      message += ` Reason: ${reason}.`;
    }
    
    message += ' If you need to reschedule, please contact us or use the scheduling system to book a new appointment.';
    
    return message;
  }

  // Get schedule by ID
  async getSchedule(schedulingId) {
    try {
      const schedule = this.schedules.get(schedulingId);
      if (schedule) {
        return schedule;
      }
      
      // Try loading from storage
      return await this.loadScheduleFromStorage(schedulingId);
    } catch (error) {
      console.error('Error getting schedule:', error);
      return null;
    }
  }

  // Get appointments for candidate
  async getCandidateAppointments(candidateId) {
    try {
      const appointments = [];
      
      for (const [schedulingId, schedule] of this.schedules) {
        if (schedule.candidateId === candidateId) {
          appointments.push(...schedule.appointments);
        }
      }
      
      return appointments.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    } catch (error) {
      console.error('Error getting candidate appointments:', error);
      return [];
    }
  }

  // Storage methods
  async saveScheduleToStorage(schedulingId, schedule) {
    try {
      await AsyncStorage.setItem(`schedule_${schedulingId}`, JSON.stringify(schedule));
    } catch (error) {
      console.error('Error saving schedule:', error);
    }
  }

  async loadScheduleFromStorage(schedulingId) {
    try {
      const scheduleData = await AsyncStorage.getItem(`schedule_${schedulingId}`);
      return scheduleData ? JSON.parse(scheduleData) : null;
    } catch (error) {
      console.error('Error loading schedule:', error);
      return null;
    }
  }

  async saveNotificationToStorage(notificationId, notification) {
    try {
      await AsyncStorage.setItem(`notification_${notificationId}`, JSON.stringify(notification));
    } catch (error) {
      console.error('Error saving notification:', error);
    }
  }
}

export default new SchedulingService();

